import NarrativeLogo from '@/components/Work/logos/NarrativeLogo';
import heroVideo from '@/assets/cases/narrative/0.mov';
import heroMainVideo from '@/assets/cases/narrative/00.mov';
import heroMainBlur from '@/assets/cases/narrative/00.blur.webp';
import waitlistSignup from '@/assets/cases/narrative/1.mov';
import problemManifesto from '@/assets/cases/narrative/2a.mov';
import featureAnimations from '@/assets/cases/narrative/2b.mov';
import featureCarousel from '@/assets/cases/narrative/3.mov';
import loginScreen from '@/assets/cases/narrative/3b.jpg';
import butterStorybook from '@/assets/cases/narrative/5a.jpg';
import butterLibrary from '@/assets/cases/narrative/5b.jpg';
import brandExtractor from '@/assets/cases/narrative/6a.mov';
import brandConfiguration from '@/assets/cases/narrative/6b.mov';
import mcpLayer from '@/assets/cases/narrative/7a.mov';
import skeletonBlockEditor from '@/assets/cases/narrative/7b.mov';
import slideEditor from '@/assets/cases/narrative/8.mov';
import {CaseStudy} from './types';

const narrativeAI: CaseStudy = {
    // ============================================
    // FIXED METADATA
    // ============================================
    id: 'narrative-ai-mvp',
    slug: 'narrative-text-to-slides-mvp',
    title: 'Narrative',
    client: 'Narrative AI',
    industry: 'AI / Presentation Software',
    projectType: 'Zero-to-One MVP Development',
    timeline: '8 weeks',
    technologies: [
        'React',
        'TypeScript',
        'Next.js',
        'Design Systems',
        'LLM Integration',
        'Data Connectors',
        'Programmatic Design'
    ],

    shortDescription: 'Proving what AI can do. The market solves text-to-slides wrong—we built it right.',
    logo: NarrativeLogo,
    featured: true,
    workHighlight: {
        id: 'narrative',
        mediaType: 'video',
        media: heroMainVideo,
        mediaSources: [{src: heroMainVideo, type: 'video/quicktime'}],
        placeholder: heroMainBlur
    },

    // ============================================
    // HERO SECTION DATA
    // ============================================
    heroStatement:
        'Narrative AI proving the market solves text-to-slides wrong. We built skeleton-first editing, code-driven design system, data connectors, and data visualization. Eight weeks, full MVP.',
    heroMetrics: [
        {
            label: 'Branded slides per user',
            value: '500+'
        },
        {
            label: 'Data sources',
            value: '10+'
        },
        {
            label: 'MVP timeline',
            value: '8 weeks'
        }
    ],
    heroNarrative: 'The market goes prompt-to-slides. Users can\'t trust that—they need structure first. No one matches brand because they lack design systems. No one connects to data sources. Everyone uses template themes.\n\nWe built it right. Skeleton-first for structure control. Design system generating 500+ branded slides per user. AI connectors to Jira, Sheets, Snowflake. Data visualization that works. Eight weeks.',
    heroGalleryImage: heroVideo,

    // ============================================
    // SCOPE OF WORK (4 Service Areas)
    // ============================================
    scopeOfWork: [
        {
            icon: 'palette',
            title: 'Brand Design',
            number: '01',
            description: 'Created complete visual system with automated brand extraction from URLs. Code-driven design system applies brand programmatically to 500+ slide variations—template-based tools can\'t do this.'
        },
        {
            icon: 'design_services',
            title: 'UI/UX Design',
            number: '02',
            description: 'Designed skeleton-first editor separating content from design, data connector interfaces for external sources, and deck editor with real-time preview. Users control structure before committing to slides.'
        },
        {
            icon: 'code',
            title: 'Full Stack Engineering',
            number: '03',
            description: 'Built complete platform with skeleton CMS, programmatic slide generation, brand ingestion pipeline, and export engine. Eight weeks, production-ready MVP with 50+ features.'
        },
        {
            icon: 'psychology',
            title: 'AI / ML Engineering',
            number: '04',
            description: 'Implemented LLM integration for content generation, data connectors to Jira/Sheets/Snowflake, and intelligent slide-layout matching. AI speaks to where data lives.'
        }
    ],

    // ============================================
    // CASE DETAILS (Collapsible Card)
    // ============================================
    caseDetails: {
        task: 'Narrative AI demonstrating what AI makes possible when you solve text-to-slides correctly. The market skips structure, can\'t match brand, ignores data sources, uses template themes, and fails at data visualization.',
        solution: 'Built skeleton-first editor, code-driven design system generating 500+ branded slides per user, AI connectors to Jira/Sheets/Snowflake, and visualization engine. Eight weeks, full MVP.',
        result: 'Delivered complete platform proving the market solves this wrong. Structure before design. Design systems for brand matching. Data connectors. Unlimited variations. Automated data viz.',
        stack: [
            'React',
            'TypeScript',
            'Next.js',
            'Design Systems',
            'LLM Integration',
            'Data Connectors',
            'Chart Generation'
        ]
    },

    // ============================================
    // DYNAMIC CONTENT (Visual Storytelling)
    // ============================================
    content: [
        // THE MARKET PROBLEM
        {
            type: 'heading2',
            content: 'Why The Market Fails',
            id: 'market',
            description: 'Five critical mistakes every text-to-slides tool makes'
        },
        {
            type: 'paragraph',
            content: 'Every AI presentation tool on the market makes the same mistakes. They go prompt-to-slides immediately when users need structure first. They can\'t match brand because they lack design systems. They ignore where your data lives—Jira, Sheets, Snowflake. They lock you into template themes. They fail at data visualization. We built this to prove you can solve all five correctly.'
        },

        {
            type: 'comparisonTable',
            content: {
                headers: ['Market Approach', 'Our Approach'],
                rows: [
                    ['Prompt → slides immediately. Users can\'t control structure.', 'Prompt → skeleton → slides. Users own the outline before design happens.'],
                    ['No design systems. Brand matching is manual and inconsistent.', 'Code-driven design system. 500+ slides branded automatically per user.'],
                    ['Data lives in their system. Copy-paste everything.', 'AI connects to your data—Jira, Sheets, Snowflake. Bring data, not copies.'],
                    ['Template themes lock you into predefined styles.', 'Programmatic generation. Unlimited variations, always on-brand.'],
                    ['Data visualization fails. Charts look generic, off-brand.', 'Chart engine with brand styling. Data viz that actually works.']
                ]
            }
        },

        {
            type: 'divider'
        },

        // BRAND DESIGN
        {
            type: 'heading2',
            content: 'Brand Design',
            id: 'brand',
            description: 'Communicating future while converting to waitlist'
        },
        {
            type: 'paragraph',
            content: 'There\'s a lot of pain around slides. Every tool promises AI magic but delivers template mediocrity. The brand needed to communicate future—this is what presentation software becomes with real AI. But the landing page needed to convert—waitlist signups, not just brand impressions. Future vs conversion. We solved both.'
        },

        // LANDING PAGE
        {
            type: 'gallery',
            content: {
                heading: 'Landing Page Experience',
                caption: 'Waitlist signup, problem manifesto, feature animations, and feature carousel—balancing vision with conversion.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: waitlistSignup,
                                alt: 'Waitlist signup interface',
                                caption: 'Waitlist signup: conversion-focused CTA',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                src: problemManifesto,
                                alt: 'Problem manifesto section',
                                caption: 'Problem manifesto: articulating market failures',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            },
                            {
                                src: featureAnimations,
                                alt: 'Feature animations',
                                caption: 'Feature animations: showing what\'s possible',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: featureCarousel,
                                alt: 'Feature carousel showcase',
                                caption: 'Feature carousel: comprehensive capability demonstration',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: loginScreen,
                                alt: 'Login screen interface',
                                caption: 'Login screen: clean authentication experience',
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        // PLATFORM CAPABILITIES
        {
            type: 'heading2',
            content: 'Platform Capabilities',
            id: 'platform',
            description: 'Four core systems the market can\'t replicate'
        },
        {
            type: 'paragraph',
            content: 'Eight weeks, full MVP. Design system for programmatic brand application. Brand extraction from URLs. Skeleton-first editor for structure control. Slide editor with real-time preview. This is what AI makes possible when you solve problems correctly.'
        },

        {
            type: 'gallery',
            content: {
                heading: 'Butter Design System',
                caption: 'Code-driven design system with programmatic brand application. 500+ slides per user, all perfectly branded.',
                rows: [
                    {
                        layout: 'double',
                        images: [
                            {
                                src: butterStorybook,
                                alt: 'Storybook design system',
                                caption: 'Storybook where entire slide design system was built'
                            },
                            {
                                src: butterLibrary,
                                alt: 'Slide library',
                                caption: 'Hundreds of slides respecting brand, powered by design system'
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'gallery',
            content: {
                heading: 'Brand Extraction',
                caption: 'Automated brand scraping from URLs—zero manual configuration.',
                rows: [
                    {
                        layout: 'double',
                        images: [
                            {
                                src: brandExtractor,
                                alt: 'Brand extractor interface',
                                caption: 'Paste URL, system extracts brand automatically',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            },
                            {
                                src: brandConfiguration,
                                alt: 'Brand configuration and preview',
                                caption: 'Configure brand settings and preview slides',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'gallery',
            content: {
                heading: 'Skeleton Editor',
                caption: 'Structure-first content editing—AI suggests outline, users control before committing to slides.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: mcpLayer,
                                alt: 'MCP integration layer',
                                caption: 'MCP layer connecting to Jira, Google Drive, Airtable',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        images: [
                            {
                                src: skeletonBlockEditor,
                                alt: 'Skeleton block editor',
                                caption: 'Block-focused editor so users can focus on content',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'gallery',
            content: {
                heading: 'Slide Editor',
                caption: 'Click generate, watch the LLM build slides in real-time—all perfectly branded with data visualization.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: slideEditor,
                                alt: 'LLM generating slides',
                                caption: 'Real-time slide generation: click generate, watch LLM build deck',
                                kind: 'video',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true,
                            }
                        ]
                    }
                ]
            }
        },

        {
            type: 'divider'
        },

        // TECHNICAL FEATS
        {
            type: 'heading2',
            content: 'Technical Feats',
            id: 'technical',
            description: 'LLM engineering and integration architecture'
        },
        {
            type: 'paragraph',
            content: 'Most tools bolt AI onto existing architectures. We built AI-native from the start. Forced LLM to speak our JSON schema for deck generation. Hooked up reusable MCPs for external tool integration in the chat interface. Built intelligent slide selection with proper indexing. This is what modern LLM engineering looks like when you do it right.'
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'Structured LLM Output',
                description: 'Market lets LLMs generate unstructured text. We force JSON schema compliance.',
                points: [
                    {
                        icon: 'code',
                        title: 'Schema-Driven Generation',
                        text: 'LLM outputs valid JSON matching our deck schema. No parsing failures, no format errors. Structured output from the start—decks generate reliably every time.'
                    },
                    {
                        icon: 'check_circle',
                        title: 'Type-Safe Responses',
                        text: 'TypeScript types match LLM output schema exactly. Frontend knows what to expect, backend validates automatically. Zero runtime type errors from AI responses.'
                    },
                    {
                        icon: 'psychology',
                        title: 'Prompt Engineering',
                        text: 'Prompts engineered to enforce schema compliance without degrading quality. LLM understands deck structure, content hierarchy, and brand requirements in one shot.'
                    }
                ]
            }
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'MCP Integration Architecture',
                description: 'Market builds custom integrations. We use Model Context Protocol for reusable tool connections.',
                points: [
                    {
                        icon: 'hub',
                        title: 'Reusable MCP Connectors',
                        text: 'Chat interface connects to external tools via MCP—Jira, Sheets, Snowflake. Add new data source, drop in MCP, no custom integration code required.'
                    },
                    {
                        icon: 'settings',
                        title: 'Tool Discovery',
                        text: 'LLM discovers available tools automatically through MCP. User mentions Jira ticket, LLM knows how to fetch it. Context-aware tool selection without hardcoding.'
                    },
                    {
                        icon: 'arrow_forward',
                        title: 'Seamless Tool Execution',
                        text: 'LLM calls tools mid-conversation, incorporates results naturally. User never leaves chat—data flows in from external sources as needed.'
                    }
                ]
            }
        },

        {
            type: 'keyBulletList',
            content: {
                heading: 'Intelligent Slide Selection',
                description: 'Market generates slides randomly. We built indexing and selection logic.',
                points: [
                    {
                        icon: 'search',
                        title: 'Slide Library Indexing',
                        text: 'Every slide component indexed by content type, layout pattern, data requirements. LLM queries index, finds best match for content structure.'
                    },
                    {
                        icon: 'auto_awesome',
                        title: 'Content-to-Layout Mapping',
                        text: 'LLM analyzes content blocks, selects appropriate slide layouts automatically. Text-heavy content gets different layouts than data visualizations—intelligent matching every time.'
                    },
                    {
                        icon: 'tune',
                        title: 'Context-Aware Selection',
                        text: 'Slide selection considers brand tokens, previous selections, deck flow. No duplicate layouts in sequence, visual variety maintained, brand consistency enforced.'
                    }
                ]
            }
        }
    ]
};

export default narrativeAI;
